// AppLogic.tsx
import { h, FunctionalComponent } from "preact";
import { useEffect, useState } from "preact/hooks";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import {
  PingSession,
  startSession,
  endSession,
  getEffectiveUserId,
  getEffectiveToken,
  registerOpenTab,
  unregisterOpenTab,
  OPEN_TAB_REFRESH_MS,
} from "../../utils";
import {
  setSessionId,
  setSessionStartFailing,
} from "../../redux/slices/globalsSlice";
import logger from "../../logger";

// Identity note (issue #1733): on edge, login stores the user in localStorage
// and reloads; unlike the Auth0/anon/widget paths, nothing dispatches
// setUserId/setJWTUserIdToken, so globals.userId/jwtUserIdToken stay empty. The
// session-start gate below would then never pass on edge and no sessionId would
// be minted, leaving every analytics event with an empty session_id (blank
// "Session Id" in the edge CSV). getEffectiveUserId/getEffectiveToken fall back
// to the edge login session; in cloud they reduce to the redux globals. They
// read localStorage, so they are called lazily inside the effects/handlers
// below rather than on every render.
// Delays before each retry of a failed session start. After the last one it
// keeps retrying at that interval: search waits for a session, so stopping
// would leave the page unable to search until a reload.
const SESSION_START_RETRY_MS = [2000, 5000, 15000, 30000];
// With no session this long after the first start went out (the first try and
// the 2 s and 5 s retries), edge search stops waiting and runs without one.
// Time, not a count of failures: a start that hangs instead of failing would
// otherwise hold search for minutes. The retries go on, and once a session
// starts, later events carry it.
const SEARCH_WITHOUT_SESSION_AFTER_MS = 7000;

const AppLogic: FunctionalComponent = () => {
  const appearance = useAppSelector((state) => state.appearance);
  const search = useAppSelector((state) => state.search);
  const globals = useAppSelector((state) => state.globals);
  const alias = useAppSelector((state) => state.alias);
  const auth = useAppSelector((state) => state.auth);

  const dispatch = useAppDispatch();
  const [tabStatusChanged, SetStatusChanged] = useState<boolean>(false);

  // Update global token for logging when JWT token changes
  useEffect(() => {
    if (globals.jwtUserIdToken) {
      (global as any).token = globals.jwtUserIdToken;
    }
  }, [globals.jwtUserIdToken]);

  useEffect(() => {
    const handleVisibilityChange = async () => {
      const effectiveUserId = getEffectiveUserId();
      const effectiveToken = getEffectiveToken();
      if (
        alias.workspaceId &&
        alias.portalId &&
        globals.sessionId &&
        effectiveUserId
      ) {
        try {
          if (!document.hidden) {
            const pingResponse = await PingSession(
              alias.workspaceId,
              alias.portalId,
              globals.sessionId,
              effectiveUserId,
              effectiveToken,
              false,
            );
            pingResponse && dispatch(setSessionId(pingResponse?.sessionId));
            logger.log({
              message: "Ping session response (visibility change)",
              ping_response: pingResponse,
              portal_alias: alias.portalAlias,
              workspace_id: alias.workspaceId,
              session_id: globals.sessionId,
            });
          }
        } catch (error) {
          logger.error({
            message: "Error in ping session visibility change handler",
            error: error,
            stacktrace: (error as any)?.stack,
            portal_alias: alias.portalAlias,
            workspace_id: alias.workspaceId,
            session_id: globals.sessionId,
          });
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [alias, globals]);

  useEffect(() => {
    let cancelled = false;
    let retry: ReturnType<typeof setTimeout> | undefined;
    let giveUpWaiting: ReturnType<typeof setTimeout> | undefined;
    const attempt = async (n: number) => {
      const effectiveUserId = getEffectiveUserId();
      const effectiveToken = getEffectiveToken();
      if (!alias.workspaceId || !alias.portalId || !effectiveUserId) return;
      if (globals.sessionId) return;
      if (n === 0)
        giveUpWaiting = setTimeout(() => {
          if (!cancelled) dispatch(setSessionStartFailing(true));
        }, SEARCH_WITHOUT_SESSION_AFTER_MS);
      try {
        const startSessionResponse = await startSession(
          alias.workspaceId,
          alias.portalId,
          effectiveUserId,
          effectiveToken,
          false,
        );
        if (startSessionResponse?.sessionId) {
          if (!cancelled) {
            clearTimeout(giveUpWaiting);
            dispatch(setSessionId(startSessionResponse.sessionId));
            dispatch(setSessionStartFailing(false));
          } else if (!getEffectiveUserId())
            // Logged out while this start was in flight: nothing will hold
            // its session, so end it now. Only on logout. A re-run for the
            // same user (its token arriving) gets this same session back from
            // the server, and an identity swap without a logout (a public
            // portal's anonymous id replacing a widget host's user) may leave
            // it in use by that user's other tabs; both are left to the idle
            // sweep, as before.
            endSession(
              alias.workspaceId,
              alias.portalId,
              effectiveUserId,
              startSessionResponse.sessionId,
              true,
            );
          return;
        }
      } catch (error) {
        logger.error({
          message: "Error starting session",
          error: error,
          stacktrace: (error as any)?.stack,
          portal_alias: alias.portalAlias,
          workspace_id: alias.workspaceId,
          user_id: globals.userId,
          attempt: n + 1,
        });
      }
      // Search waits for a sessionId (canStartSearch), so a start that failed
      // at page load must be retried, or the page can't search until a reload.
      if (!cancelled) {
        const last = SESSION_START_RETRY_MS.length - 1;
        retry = setTimeout(
          () => attempt(n + 1),
          SESSION_START_RETRY_MS[Math.min(n, last)],
        );
      }
    };
    attempt(0);
    return () => {
      cancelled = true;
      clearTimeout(retry);
      clearTimeout(giveUpWaiting);
    };
    // Re-run when identity (cloud login, or edge login hydrating the JWT) or
    // the resolved portal changes; getEffectiveUserId() reads the edge login
    // session lazily at that point.
  }, [
    globals.userId,
    globals.jwtUserIdToken,
    alias.workspaceId,
    alias.portalId,
  ]);

  useEffect(() => {
    if (alias.workspaceId && alias.portalId && globals.sessionId) {
      const intervalId = setInterval(async () => {
        const effectiveUserId = getEffectiveUserId();
        const effectiveToken = getEffectiveToken();
        if (!effectiveUserId) return;
        try {
          if (!document.hidden) {
            const pingResponse = await PingSession(
              alias.workspaceId,
              alias.portalId,
              globals.sessionId,
              effectiveUserId,
              effectiveToken,
              false,
            );
            pingResponse && dispatch(setSessionId(pingResponse?.sessionId));
            logger.log({
              message: "Ping session response (interval)",
              ping_response: pingResponse,
              portal_alias: alias.portalAlias,
              workspace_id: alias.workspaceId,
              session_id: globals.sessionId,
            });
          }
        } catch (error) {
          logger.error({
            message: "Error in ping session interval handler",
            error: error,
            stacktrace: (error as any)?.stack,
            portal_alias: alias.portalAlias,
            workspace_id: alias.workspaceId,
            session_id: globals.sessionId,
          });
        }
      }, 60000); // 60000 ms = 1 minute

      // Cleanup interval on component unmount
      return () => clearInterval(intervalId);
    }
  }, [alias.workspaceId, alias.portalId, globals.sessionId, globals.userId]);

  // Session end on tab/window close (issue #1733). pagehide is the reliable
  // unload signal (fires on close, refresh, and full-page-redirect logout such
  // as Auth0's); SPA logouts that only route client-side end the session
  // explicitly via Utils.*Logout instead. Skip bfcache navigations (persisted) —
  // the page may be restored, so the session isn't really over. A refresh or a
  // same-tab navigation also fires pagehide; session_manager holds this end for
  // a short grace window, and the reloaded page's start/ping cancels it.
  //
  // Multi-tab: sessions are keyed by (userId, portalId), so several tabs share
  // one sessionId. Register this tab and only end the session on pagehide when
  // this tab was the last one open, or closing one tab would end the session
  // under the others. Explicit logout ends the session regardless of tab count,
  // so Utils.*Logout does not go through this refcount.
  useEffect(() => {
    if (!alias.workspaceId || !alias.portalId || !globals.sessionId) return;
    const workspaceId = alias.workspaceId;
    const portalId = alias.portalId;
    const sessionId = globals.sessionId;
    const effectiveUserId = getEffectiveUserId();
    registerOpenTab(workspaceId, portalId, effectiveUserId);
    // Keep this tab's entry fresh, or a tab left open longer than the stale
    // window is pruned by another tab, and closing that other tab ends the
    // session under this one.
    const refresh = setInterval(
      () => registerOpenTab(workspaceId, portalId, effectiveUserId),
      OPEN_TAB_REFRESH_MS,
    );
    const handlePageHide = (event: PageTransitionEvent) => {
      if (event.persisted) return;
      if (unregisterOpenTab(workspaceId, portalId, effectiveUserId)) {
        endSession(workspaceId, portalId, effectiveUserId, sessionId);
      }
    };
    window.addEventListener("pagehide", handlePageHide);
    return () => {
      clearInterval(refresh);
      window.removeEventListener("pagehide", handlePageHide);
      // Leave the set for the old scope when it changes (e.g. the user id), so
      // it isn't held open by a tab that no longer shares it. Not an end: this
      // tab is still open, and re-registers under the new scope.
      unregisterOpenTab(workspaceId, portalId, effectiveUserId);
    };
  }, [alias.workspaceId, alias.portalId, globals.sessionId, globals.userId]);

  return null; // No UI rendering needed here
};

export default AppLogic;
